import { FC, useCallback, useRef, useState } from 'react';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';

import styles from './ImageNodeView.module.scss';

const MIN_WIDTH = 80;

const ImageNodeView: FC<NodeViewProps> = ({ node, updateAttributes, selected, editor }) => {
  const isEditable = editor.isEditable;
  const { src, alt, width } = node.attrs;
  const containerRef = useRef<HTMLDivElement>(null);
  const [resizing, setResizing] = useState(false);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, direction: 'left' | 'right') => {
      e.preventDefault();
      e.stopPropagation();
      setResizing(true);

      const startX = e.clientX;
      const startWidth = containerRef.current?.offsetWidth ?? width ?? 400;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const diff = direction === 'right' ? moveEvent.clientX - startX : startX - moveEvent.clientX;
        const newWidth = Math.max(MIN_WIDTH, startWidth + diff);
        updateAttributes({ width: Math.round(newWidth) });
      };

      const handleMouseUp = () => {
        setResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [width, updateAttributes],
  );

  return (
    <NodeViewWrapper className={styles.imageNodeView} data-drag-handle=''>
      <div
        ref={containerRef}
        className={`${styles.imageContainer} ${isEditable && selected ? styles.selected : ''} ${resizing ? styles.resizing : ''}`}
        style={{ width: width ? `${width}px` : undefined }}
        draggable={!resizing}
      >
        <img src={src ?? ''} alt={alt ?? ''} draggable={false} />
        {isEditable && selected && !resizing && (
          <>
            <div
              className={`${styles.resizeHandle} ${styles.handleLeft}`}
              onMouseDown={(e) => handleResizeStart(e, 'left')}
              draggable={false}
            />
            <div
              className={`${styles.resizeHandle} ${styles.handleRight}`}
              onMouseDown={(e) => handleResizeStart(e, 'right')}
              draggable={false}
            />
          </>
        )}
        {isEditable && selected && width && <div className={styles.widthLabel}>{width}px</div>}
      </div>
    </NodeViewWrapper>
  );
};

export default ImageNodeView;
